import React from 'react';
import { useSelector } from 'react-redux';
import { Avatar } from '../../../../../../../components';
import {
  DetailedAnalysisEmail,
  DetailedAnalysisEmailParticipant,
} from '../../../../../../../store/deals/deals.types';
import { RootState } from '../../../../../../../store/store';
import { DetailedViewTooltip } from '../../../tooltip/DetailedViewTooltip';
import { UpperHandFeedback } from './components/upperHandFeedback/UpperHandFeedback';

type Props = {
  participants?: {
    leftParticipant: DetailedAnalysisEmailParticipant;
    rightParticipant: DetailedAnalysisEmailParticipant;
  };
  dominant?: 'left' | 'right' | 'unresolved' | null;
  currentEmail: DetailedAnalysisEmail | null;
};

export const UpperHand: React.FC<Props> = ({
  participants,
  dominant,
  currentEmail,
}): JSX.Element => {
  const user = useSelector((state: RootState) => state.auth.user);

  const leftParticipant = participants?.leftParticipant;
  const rightParticipant = participants?.rightParticipant;

  const attrs = {
    wrapper: { className: 'upper-hand' },
    header: { className: 'upper-hand__header' },
    body: {
      wrapper: { className: 'upper-hand__body' },
      leftPerson: {
        wrapper: { className: 'upper-hand__body__left-person' },
        name: {
          className:
            dominant === 'left'
              ? 'active upper-hand__body__left-person-name'
              : 'upper-hand__body__left-person-name',
        },
        avatar: {
          image: leftParticipant?.metadata?.avatarSrc
            ? leftParticipant.metadata.avatarSrc
            : '',
          userName: leftParticipant?.metadata.name,
          className:
            dominant === 'left'
              ? 'bigger upper-hand__body__left-person-avatar'
              : 'upper-hand__body__left-person-avatar',
          style: {
            cursor: 'default',
            width: dominant === 'left' ? '63px' : '34px',
            height: dominant === 'left' ? '63px' : '34px',
            minWidth: '34px',
            minHeight: '34px',
            margin: '0px 2px 4px 2px',
            paddingTop:
              dominant !== 'left' && !leftParticipant?.metadata?.avatarSrc
                ? '3px'
                : '0px',
            borderRadius: '50%',
            boxShadow: '0px 4px 4px #CCD5F6',
            border: dominant === 'left' ? '2px solid #4EB4FF' : 'none',
            fontSize: dominant === 'left' ? '18px' : '12px',
            transition: 'all 0.6s ease-in-out 0.4s',
          },
        },
      },
      rightPerson: {
        wrapper: { className: 'upper-hand__body__right-person' },
        avatar: {
          image: rightParticipant?.metadata?.avatarSrc
            ? rightParticipant.metadata.avatarSrc
            : '',
          userName: rightParticipant?.metadata.name,
          className:
            dominant === 'right'
              ? 'bigger upper-hand__body__right-person-avatar'
              : 'upper-hand__body__right-person-avatar',
          style: {
            cursor: 'default',
            width: dominant === 'right' ? '63px' : '34px',
            height: dominant === 'right' ? '63px' : '34px',
            minWidth: '34px',
            minHeight: '34px',
            borderRadius: '50%',
            margin: '0px 2px 4px 2px',
            paddingTop:
              dominant !== 'right' && !rightParticipant?.metadata?.avatarSrc
                ? '3px'
                : '0px',
            boxShadow: '0px 4px 4px #CCD5F6',
            border: dominant === 'right' ? '2px solid #4EB4FF' : 'none',
            fontSize: dominant === 'right' ? '18px' : '12px',
            transition: 'all 0.6s ease-in-out 0.4s',
          },
        },
        name: {
          className:
            dominant === 'right'
              ? 'active upper-hand__body__right-person-name'
              : 'upper-hand__body__right-person-name',
        },
      },
    },
  };

  const defineName = (name?: string) => {
    let nameResult = '';
    if (user?.name && name) {
      if (name === user?.name) {
        nameResult = 'Me';
      } else if (name.split(' ').length > 1) {
        const firstName = name.split(' ')[0];
        nameResult = firstName;
      } else nameResult = name;
    }
    return nameResult;
  };

  const leftParticipantName = defineName(leftParticipant?.metadata.name);

  const rightParticipantName = defineName(rightParticipant?.metadata.name);

  const defineTooltipText = () => {
    let message = 'Upper hand cannot be determined yet';
    if (
      (dominant === 'left' && leftParticipant?.isUser) ||
      (dominant === 'right' && rightParticipant?.isUser)
    ) {
      message = 'You have the Upper Hand in this message';
    } else if (dominant === 'left') {
      message = `${leftParticipantName} has the Upper Hand in this message`;
    } else if (dominant === 'right') {
      message = `${rightParticipantName} has the Upper Hand in this message`;
    }
    return message;
  };
  const tooltipText = defineTooltipText();
  const upperHandFeedback = currentEmail?.communicationType !== 'internal' && (
    <UpperHandFeedback />
  );

  return (
    <div {...attrs.wrapper}>
      <div {...attrs.header}>
        Upper Hand <DetailedViewTooltip text={tooltipText} />
        {upperHandFeedback}
      </div>
      <div {...attrs.body.wrapper}>
        <div {...attrs.body.leftPerson.wrapper}>
          <span {...attrs.body.leftPerson.name}>{leftParticipantName}</span>
          <Avatar {...attrs.body.leftPerson.avatar} />
        </div>
        <div {...attrs.body.rightPerson.wrapper}>
          <Avatar {...attrs.body.rightPerson.avatar} />
          <span {...attrs.body.rightPerson.name}>{rightParticipantName}</span>
        </div>
      </div>
    </div>
  );
};
